import {z} from "zod";

const referenceSchema = z.object({
  "@id": z.string().url(),
})

export const imageObjectDataSchema = z.object({
  "@type": z.literal("ImageObject"),
  url: z.string().url(),
})

export type ImageObjectData = z.infer<typeof imageObjectDataSchema>


export const postalAddressDataSchema = z.object({
  "@type": z.literal("PostalAddress"),
  // addressCountry + addressLocality are the minimum bar for a useful
  // PostalAddress; below that the address is omitted (events fall back to
  // their free-text location).
  "addressCountry": z.string().min(1),
  "addressLocality": z.string().min(1),
  "streetAddress": z.string().min(1).optional(),
  // Address extension such as a building, apartment number or C/O.
  "extendedAddress": z.string().min(1).optional(),
  "postalCode": z.string().min(1).optional(),
  "addressRegion": z.string().min(1).optional(),
})

export type PostalAddressData = z.infer<typeof postalAddressDataSchema>


export const organisationDataSchema = z.object({
  "@type": z.literal("Organization"),
  "@id": z.string().url().optional(),
  name: z.string(),
  url: z.string().url().optional(),
  sameAs: z.array(z.string().url()).nonempty().optional(),
  email: z.string().email().optional(),
  address: postalAddressDataSchema.optional(),
})

export type OrganisationData = z.infer<typeof organisationDataSchema>


export const webSiteDataSchema = z.object({
  "@type": z.literal("WebSite"),
  "@id": z.string().url().optional(),
  name: z.string(),
  url: z.string().url(),
  publisher: referenceSchema.optional(),
  inLanguage: z.string().optional(),
})

export type WebSiteData = z.infer<typeof webSiteDataSchema>


export const webPageDataSchema = z.object({
  "@type": z.literal("WebPage"),
  "@id": z.string().url(),
  url: z.string().url(),
  name: z.string(),
  description: z.string().optional(),
  inLanguage: z.string().optional(),
  isPartOf: referenceSchema.optional(),
  primaryImageOfPage: imageObjectDataSchema.optional(),
  about: z.array(referenceSchema).optional(),
})

export type WebPageData = z.infer<typeof webPageDataSchema>


export const breadCrumbListDataSchema = z.object({
  "@type": z.literal("BreadcrumbList"),
  itemListElement: z.array(z.object({
    "@type": z.literal("ListItem"),
    position: z.number().int(),
    name: z.string(),
    item: z.string().url(),
  }))
})

export type breadCrumbListData = z.infer<typeof breadCrumbListDataSchema>


export const productDataSchema = z.object({
  // Multi-typed node: the product is one entity seen as both Product and
  // MedicalDevice. One node with one @id keeps the graph unfragmented and
  // unlocks the properties of both types.
  "@type": z.tuple([z.literal("Product"), z.literal("MedicalDevice")]),
  "@id": z.string().url().optional(),
  name: z.string(),
  description: z.string().min(1).optional(),
  image: z.string().url().optional(),
  // `owner` is a Thing-level property (range Organization/Person);
  // `manufacturer` is Product-only and valid here via the Product type.
  owner: referenceSchema.optional(),
  manufacturer: referenceSchema.optional(),
})

export type ProductData = z.infer<typeof productDataSchema>


const dateOrDateTime = z.union([z.string().datetime(), z.string().date()])

export const eventDataSchema = z.object({
  "@type": z.literal("Event"),
  name: z.string().min(1),
  startDate: dateOrDateTime,
  endDate: dateOrDateTime.optional(),
  description: z.string().min(1).optional(),
  location: z.string().min(1).optional(),
})

export type EventData = z.infer<typeof eventDataSchema>


const personSchema = z.object({
  "@type": z.literal("Person"),
  name: z.string().min(1),
  jobTitle: z.string().min(1).optional(),
})

export const articleDataSchema = z.object({
  "@type": z.union([z.literal("NewsArticle"), z.literal("BlogPosting")]),
  "@id": z.string().url(),
  headline: z.string().min(1),
  datePublished: dateOrDateTime,
  dateModified: dateOrDateTime.optional(),
  image: z.string().url().optional(),
  inLanguage: z.string().optional(),
  mainEntityOfPage: referenceSchema,
  publisher: referenceSchema.optional(),
  author: z.union([personSchema, referenceSchema]).optional(),
})

export type ArticleData = z.infer<typeof articleDataSchema>
